import { gql } from '@apollo/client/core';

export const PeopleFragment = gql`
  fragment PeopleFragment on qx_Person {
    id
    externalPersonID
    createdAt
    updatedAt
    roleID
    role {
      id
      name
    }
    email
    employeeID
    title
    suffix
    firstName
    lastName
    toGroups {
      group {
        id
        name
      }
    }
    place {
      id
      name
      type
      addresses
    }
    salutation
    avatar_md
    department
    credentials {
      id
      pin
      value
      validFrom
      validTo
      enabled
      extendedTimeEnabled
      externalSync
    }
  }
`;

export const SinglePersonFragment = gql`
  fragment SinglePersonFragment on qx_Person {
    id
    externalPersonID
    roleExternalSync
    createdAt
    updatedAt
    roleID
    role {
      id
      name
    }
    email
    employeeID
    title
    suffix
    firstName
    lastName
    lockdownImmunity
    placeID
    place {
      id
      name
      type
      addresses
      hierarchy {
        name
      }
      toHomePlaces {
        customerPlaceID
      }
    }
    salutation
    department
    phoneNumbers
    licensePlate
    certification
    credentials(order_by: { validFrom: asc, validTo: asc }) {
      id
      pin
      value
      validFrom
      validTo
      enabled
      extendedTimeEnabled
      externalSync
      status
      type
      reason
    }
  }
`;

export const GQL_GET_PERSON_BY_ID = gql`
  query GQL_GET_PERSON_BY_ID($id: String!) {
    person(id: $id) {
      ...SinglePersonFragment
      avatar
    }
  }
  ${SinglePersonFragment}
`;

export const GQL_SUB_PERSON_BY_ID = gql`
  subscription GQL_SUB_PERSON_BY_ID($id: String!) {
    person(id: $id) {
      ...SinglePersonFragment
    }
  }
  ${SinglePersonFragment}
`;

export const PersonToGroupsFragment = gql`
  fragment PersonToGroupsFragment on qx_Person {
    toGroups {
      group {
        id
        permissions {
          id
          placeID
          schedule {
            id
            name
            thumbnail
          }
        }
      }
    }
  }
`;

export const PersonSearchFragment = gql`
  fragment PersonSearchFragment on qx_Person {
    id
    externalPersonID
    firstName
    lastName
    email
    employeeID
    department
    title
    lockdownImmunity
    createdAt
    updatedAt
    status # credentials status computed column
    credentials {
      id
      validFrom
      validTo
      enabled
    }
    roleID
    role {
      id
      name
    }
    place {
      id
      name
      hierarchy {
        name
      }
      path
    }
  }
`;

export const GQL_PERSON_SEARCH = gql`
  query GQL_PERSON_SEARCH($where: qx_Person_bool_exp, $limit: Int, $offset: Int, $orderBy: [qx_Person_order_by!]) {
    people(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...PersonSearchFragment
      avatar_md
    }
    personAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
  ${PersonSearchFragment}
`;

export const GQL_PERSON_TO_GROUPS_SEARCH = gql`
  query GQL_PERSON_TO_GROUPS_SEARCH($where: qx_Person_bool_exp, $limit: Int, $offset: Int, $orderBy: [qx_Person_order_by!]) {
    people(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...PersonSearchFragment
      ...PersonToGroupsFragment
      avatar_md
    }
    personAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
  ${PersonSearchFragment},
  ${PersonToGroupsFragment}
`;

const PersonSearchMiniFragment = gql`
  fragment PersonSearchMiniFragment on qx_Person {
    id
    firstName
    lastName
    title
    department
    email
    avatar_sm
    place {
      id
      name
      hierarchy {
        name
      }
    }
  }
`;

export const LiveScrollerPersonResolveFragment = gql`
  fragment LiveScrollerPersonResolveFragment on qx_Person {
    id
    firstName
    lastName
    avatar_sm
  }
`;

export const AiToolResultPersonResolveFragment = gql`
  fragment AiToolResultPersonResolveFragment on qx_Person {
    id
    firstName
    lastName
    title
    status
    avatar_sm
    role {
      id
      name
    }
    place {
      id
      name
      hierarchy {
        name
      }
    }
  }
`;

export const GQL_PERSON_SEARCH_MINI = gql`
  query GQL_PERSON_SEARCH_MINI($where: qx_Person_bool_exp, $limit: Int, $offset: Int, $orderBy: [qx_Person_order_by!]) {
    people(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...PersonSearchMiniFragment
    }
    personAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
  ${PersonSearchMiniFragment}
`;

export const GQL_GET_PEOPLE_BY_IDS = gql`
  query GQL_GET_PEOPLE_BY_IDS($ids: [String!]!) {
    people(where: { id: { _in: $ids } }) {
      ...LiveScrollerPersonResolveFragment
    }
  }
  ${LiveScrollerPersonResolveFragment}
`;

export const GQL_GET_AI_TOOL_RESULT_PEOPLE_BY_IDS = gql`
  query GQL_GET_AI_TOOL_RESULT_PEOPLE_BY_IDS($ids: [String!]!) {
    people(where: { id: { _in: $ids } }) {
      ...AiToolResultPersonResolveFragment
    }
  }
  ${AiToolResultPersonResolveFragment}
`;

export const GQL_PERSON_SEARCH_SUBSCRIPTION = gql`
  subscription GQL_PERSON_SEARCH_SUBSCRIPTION(
    $where: qx_Person_bool_exp,
    $limit: Int,
    $offset: Int,
    $orderBy: [qx_Person_order_by!],
    $currentScopePath: String
  ) {
    people(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...PersonSearchFragment
    }
  }
  ${PersonSearchFragment}
`;

export const GQL_PERSON_TO_GROUPS_SEARCH_SUBSCRIPTION = gql`
  subscription GQL_PERSON_TO_GROUPS_SEARCH_SUBSCRIPTION(
    $where: qx_Person_bool_exp,
    $limit: Int,
    $offset: Int,
    $orderBy: [qx_Person_order_by!],
    $currentScopePath: String
  ) {
    people(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...PersonSearchFragment
      ...PersonToGroupsFragment
    }
  }
  ${PersonSearchFragment},
  ${PersonToGroupsFragment}
`;
